import React from 'react';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import Image from 'next/image';
import { buildTrackingId } from '@/utils/tracking-helper';
import downButton from '../../../../../../public/images/expand-button-down.svg';

const MAX_VISIBLE_ANSWERS = 6;

export default function Question(
  props: Readonly<{
    components: any;
    loadCsForm: (path: string) => {};
    addToPathForTicketDescription: (question: string, answer: string) => void;
    trackingPrefix?: string;
  }>,
) {
  const { components, loadCsForm, addToPathForTicketDescription, trackingPrefix } = props;
  const question = components?.find((component: { id: string }) => component.id === 'question');
  const answers = new Map<
    string,
    Array<{
      text: string;
      icon: { url: string; altText: string };
      nextScreenPath: string;
    }>
  >();
  components?.forEach((component: { content: any; id: string }) => {
    if (component.id === 'answers') {
      component.content?.selectedComponents.forEach(
        (c: { content: { items: { components: any[] }[] } }) => {
          const answerText = c.content.items[0].components.find(
            (answer: { id: string }) => answer.id === 'answer',
          ).content.text;
          const answerIcon = c.content.items[0].components.find(
            (answer: { id: string }) => answer.id === 'icon',
          ).content?.firstImage;
          const answerCategory =
            c.content.items[0].components.find((answer: { id: string }) => answer.id === 'category')
              .content?.text || '';
          const answerNextScreenPath = c.content.items[0].components
            .find((answer: { id: string }) => answer.id === 'next-screen')
            .content.items?.at(0)?.path;
          if (!answers.has(answerCategory)) {
            answers.set(answerCategory, [
              {
                text: answerText,
                icon: answerIcon,
                nextScreenPath: answerNextScreenPath,
              },
            ]);
          } else {
            answers.get(answerCategory)!.push({
              text: answerText,
              icon: answerIcon,
              nextScreenPath: answerNextScreenPath,
            });
          }
        },
      );
    }
  });
  const answerEntries = Array.from(answers.entries());

  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  return (
    <div className="mx-auto max-w-screen-2xl justify-center  py-8 text-center">
      <h1 className="mb-6 text-2xl font-semibold md:text-4xl">
        {formatPunctuation(question?.content.text || '')}
      </h1>

      <div className="flex flex-wrap justify-center">
        {answerEntries.map(([category, answerList]) => {
          const isExpanded = expandedCategories.has(category);
          const visibleAnswers = isExpanded ? answerList : answerList.slice(0, MAX_VISIBLE_ANSWERS);

          return (
            <div className="w-full p-2.5 md:w-1/3" key={category}>
              {category && (
                <h2 className="mb-6 text-left text-lg font-semibold md:text-2xl">{category}</h2>
              )}

              {visibleAnswers.map((k) => (
                <div
                  key={k.text}
                  className="mb-2 h-14 content-center rounded-xl border p-2.5 text-left text-md font-medium hover:cursor-pointer hover:border-primary-100"
                  onClick={() => {
                    const questionText = components.find(
                      (component: { id: string }) => component.id === 'question',
                    ).content.text;
                    const answerText = k.text;
                    addToPathForTicketDescription(`${questionText}`, `${answerText} ->`);
                    loadCsForm(k.nextScreenPath);
                  }}
                  data-tile-element-id={buildTrackingId(trackingPrefix, k.text)}
                >
                  <span className="flex content-center items-center px-2">
                    {k.icon && (
                      <img src={k.icon.url} alt={k.icon.altText} className="mr-2.5 h-8 w-8" />
                    )}
                    {k.text}
                  </span>
                </div>
              ))}

              {answerList.length > MAX_VISIBLE_ANSWERS && (
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="text-primary-600 ml-2 mt-2 flex items-center text-md font-medium underline"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                  <Image
                    src={downButton}
                    alt=""
                    className={`ml-1 h-2 w-4 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
